import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Receipt, DollarSign, RefreshCw, CheckCircle, AlertTriangle, 
  Eye, ArrowUpRight, ArrowDownLeft, Image, Clock, User, Edit, Trash2, X, Save
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const ReceiptsTab = () => {
  const [receipts, setReceipts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processAmount, setProcessAmount] = useState('');
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editForm, setEditForm] = useState({ receipt_amount: '', notes: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const [receiptsRes, statsRes] = await Promise.all([
        api.get(`/receipts${params}`),
        api.get('/receipts/stats')
      ]);
      setReceipts(receiptsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (receipt) => {
    setEditingReceipt(receipt.id);
    setEditForm({
      receipt_amount: receipt.receipt_amount.toString(),
      notes: receipt.notes || ''
    });
  };

  const handleSaveEdit = async () => {
    setProcessing(true);
    try {
      await api.put(`/receipts/${editingReceipt}`, {
        receipt_amount: parseFloat(editForm.receipt_amount),
        notes: editForm.notes
      });
      toast.success('Receipt updated successfully');
      setEditingReceipt(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update receipt');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (receiptId) => {
    if (!window.confirm('Are you sure you want to archive this receipt?')) return;
    
    setProcessing(true);
    try {
      await api.delete(`/receipts/${receiptId}`);
      toast.success('Receipt archived successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive receipt');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcess = async (receiptId, action) => {
    setProcessing(true);
    try {
      const data = { action };
      if (processAmount) {
        data.amount = parseFloat(processAmount);
      }
      await api.put(`/receipts/${receiptId}/process`, data);
      toast.success(`Receipt ${action === 'refund' ? 'refunded' : action === 'charge' ? 'charged' : 'approved'} successfully`);
      setSelectedReceipt(null);
      setProcessAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[var(--warning)]/15 text-yellow-700',
      approved: 'bg-[#D4AF37]/10 text-[#D4AF37]',
      refunded: 'bg-[var(--success)]/15 text-green-700',
      charged: 'bg-[#F4C2C2]/15 text-purple-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-[var(--background-subtle)]'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading receipts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[var(--primary)]" />
            Receipt Dashboard
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">Manage pickup service receipts and refunds</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 text-center border-l-4 border-yellow-400">
            <p className="text-3xl font-bold text-[var(--warning)]">{stats.pending_count}</p>
            <p className="text-sm text-[var(--text-muted)]">Pending Review</p>
          </div>
          <div className="card p-4 text-center border-l-4 border-green-400">
            <div className="flex items-center justify-center gap-1">
              <ArrowUpRight className="w-5 h-5 text-[var(--success)]" />
              <p className="text-3xl font-bold text-[var(--success)]">${stats.total_pending_refunds}</p>
            </div>
            <p className="text-sm text-[var(--text-muted)]">To Refund</p>
          </div>
          <div className="card p-4 text-center border-l-4 border-red-400">
            <div className="flex items-center justify-center gap-1">
              <ArrowDownLeft className="w-5 h-5 text-[var(--error)]" />
              <p className="text-3xl font-bold text-[var(--error)]">${stats.total_pending_charges}</p>
            </div>
            <p className="text-sm text-[var(--text-muted)]">To Charge</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold">{receipts.length}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Receipts</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['', 'pending', 'approved', 'refunded', 'charged'].map(s => (
          <Button
            key={s}
            variant={filter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(s)}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {/* Receipts List */}
      <div className="space-y-4">
        {receipts.length === 0 ? (
          <div className="card p-12 text-center">
            <Receipt className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No Receipts</h3>
            <p className="text-[var(--text-muted)]">Worker receipts will appear here after pickup services.</p>
          </div>
        ) : (
          receipts.map((receipt) => (
            <motion.div
              key={receipt.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card p-5 ${receipt.status === 'pending' ? 'border-2 border-yellow-300' : ''}`}
              data-testid={`receipt-${receipt.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(receipt.status)}
                    {receipt.difference > 0 && (
                      <span className="text-xs bg-[var(--success)]/15 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3" /> Refund ${receipt.difference}
                      </span>
                    )}
                    {receipt.difference < 0 && (
                      <span className="text-xs bg-[var(--error)]/15 text-red-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <ArrowDownLeft className="w-3 h-3" /> Charge ${Math.abs(receipt.difference)}
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-lg">{receipt.service_name}</h3>
                  <div className="grid md:grid-cols-3 gap-4 mt-3 text-sm">
                    <div>
                      <p className="text-[var(--text-muted)]">Estimated</p>
                      <p className="font-bold text-lg">${receipt.estimated_amount}</p>
                    </div>
                    <div>
                      <p className="text-[var(--text-muted)]">Actual Receipt</p>
                      <p className="font-bold text-lg">${receipt.receipt_amount}</p>
                    </div>
                    <div>
                      <p className="text-[var(--text-muted)]">Difference</p>
                      <p className={`font-bold text-lg ${receipt.difference > 0 ? 'text-[var(--success)]' : receipt.difference < 0 ? 'text-[var(--error)]' : ''}`}>
                        {receipt.difference > 0 ? '+' : ''}${receipt.difference}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-muted)]">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Worker: {receipt.worker_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Customer: {receipt.customer_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(receipt.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {receipt.receipt_image_url && (
                    <Button variant="outline" size="sm" onClick={() => window.open(receipt.receipt_image_url, '_blank')}>
                      <Image className="w-4 h-4 mr-1" /> View
                    </Button>
                  )}
                  {receipt.status === 'pending' && (
                    <Button size="sm" onClick={() => setSelectedReceipt(receipt)} className="btn-primary">
                      Process
                    </Button>
                  )}
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEdit(receipt)}
                      className="text-[#D4AF37]"
                      data-testid={`edit-receipt-${receipt.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(receipt.id)}
                      className="text-[var(--error)]"
                      data-testid={`delete-receipt-${receipt.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Process Modal */}
      {selectedReceipt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-6 max-w-lg w-full"
          >
            <h2 className="text-xl font-bold mb-4">Process Receipt</h2>
            
            <div className="bg-[var(--accent)] p-4 rounded-xl mb-6">
              <p className="font-bold">{selectedReceipt.service_name}</p>
              <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                <div>
                  <p className="text-[var(--text-muted)]">Estimated</p>
                  <p className="font-bold">${selectedReceipt.estimated_amount}</p>
                </div>
                <div>
                  <p className="text-[var(--text-muted)]">Actual</p>
                  <p className="font-bold">${selectedReceipt.receipt_amount}</p>
                </div>
                <div>
                  <p className="text-[var(--text-muted)]">Difference</p>
                  <p className={`font-bold ${selectedReceipt.difference >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                    ${selectedReceipt.difference}
                  </p>
                </div>
              </div>
            </div>

            {selectedReceipt.difference !== 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  {selectedReceipt.difference > 0 ? 'Refund Amount' : 'Charge Amount'}
                </label>
                <Input
                  type="number"
                  value={processAmount}
                  onChange={(e) => setProcessAmount(e.target.value)}
                  placeholder={`$${Math.abs(selectedReceipt.difference)}`}
                  step="0.01"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Leave empty to use calculated amount: ${Math.abs(selectedReceipt.difference)}
                </p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => { setSelectedReceipt(null); setProcessAmount(''); }} className="flex-1">
                Cancel
              </Button>
              {selectedReceipt.difference === 0 && (
                <Button onClick={() => handleProcess(selectedReceipt.id, 'approve')} disabled={processing} className="flex-1 bg-[#E6C965] hover:bg-blue-700 text-white">
                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                </Button>
              )}
              {selectedReceipt.difference > 0 && (
                <Button onClick={() => handleProcess(selectedReceipt.id, 'refund')} disabled={processing} className="flex-1 bg-[var(--success)] hover:bg-[var(--success)]/80 text-white">
                  <ArrowUpRight className="w-4 h-4 mr-2" /> Refund Customer
                </Button>
              )}
              {selectedReceipt.difference < 0 && (
                <Button onClick={() => handleProcess(selectedReceipt.id, 'charge')} disabled={processing} className="flex-1 bg-[var(--error)] hover:bg-[var(--error)]/80 text-white">
                  <ArrowDownLeft className="w-4 h-4 mr-2" /> Charge Customer
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Modal */}
      {editingReceipt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Edit className="w-5 h-5" /> Edit Receipt
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setEditingReceipt(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Receipt Amount ($)</label>
                <Input
                  type="number"
                  value={editForm.receipt_amount}
                  onChange={(e) => setEditForm({...editForm, receipt_amount: e.target.value})}
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  className="w-full p-3 border rounded-xl min-h-[100px] resize-none"
                  placeholder="Add notes..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setEditingReceipt(null)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={processing} className="flex-1 btn-primary">
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ReceiptsTab;
